// Imports
import { type PayloadAction, type AsyncThunkAction } from '@reduxjs/toolkit';
import { createSlice } from '../../toolkit';
import { type ExportJSON, services } from '../../../';
import {
  HandlerPayload,
  createAsyncThunkAction,
  createResponseHandler,
} from '../../helpers';
import { GENERATED_STATE } from '../../constants';
import type { AsyncThunkReturnType, GeneratedState } from '../../types';
import type { ExtractedError } from '../../../types';
import { exportEnterpriseDailyReportAdapter } from '../../adapters';
import { reset } from '../../actions';

// Service methods
export const exportEnterpriseDailyReport = createAsyncThunkAction(
  services.enterprise.exportEnterpriseDailyReport,
);

export type ExportJSONReturn = AsyncThunkReturnType<
  typeof exportEnterpriseDailyReport
>;
export type ExportJSONEntity = ExportJSONReturn[0];
export type ExportJSONParams = ExportJSONReturn[1];
export type ExportJSONConfig = ExportJSONReturn[2];

export type ExportJSONAsyncThunkAction = AsyncThunkAction<
  ExportJSONEntity,
  ExportJSONParams,
  ExportJSONConfig
>;

// Reducer
export type ExportJSONState = GeneratedState<ExportJSON>;

export const handleExportJSONResponse = createResponseHandler<
  ExportJSONState,
  ExportJSON
>(exportEnterpriseDailyReportAdapter);

export const exportEnterpriseDailyReportSlice = createSlice({
  name: 'exportEnterpriseDailyReport',
  initialState: {
    ...GENERATED_STATE,
    ...exportEnterpriseDailyReportAdapter.getInitialState(),
  },
  reducers: {
    resetService: (
      state: ExportJSONState,
      action: PayloadAction<undefined>,
    ) => ({
      ...GENERATED_STATE,
      ...exportEnterpriseDailyReportAdapter.getInitialState(),
    }),
    chainedRequest: (
      state: ExportJSONState,
      action: PayloadAction<undefined>,
    ) => ({
      ...state,
      error: action.payload,
      chainedRequest: true,
    }),
    error: (state: ExportJSONState, action: PayloadAction<ExtractedError>) => ({
      ...state,
      error: action.payload,
    }),
    fetching: (state: ExportJSONState, action: PayloadAction<undefined>) => ({
      ...state,
      isLoading: true,
    }),
    fetched: (
      state: ExportJSONState,
      action: PayloadAction<HandlerPayload<ExportJSON[]>>,
    ) => ({
      ...state,
      ...handleExportJSONResponse(state, action),
      // Don't set fetchedList when using this action, as it's used
      // by InfoEntity
      fetchedList: state.fetchedList,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(reset, (state) => ({
      ...GENERATED_STATE,
      ...exportEnterpriseDailyReportAdapter.getInitialState(),
    }));

    builder.addCase(exportEnterpriseDailyReport.pending, (state) => ({
      ...state,
      isLoading: state.isLoading,
      isSubmitting: true,
    }));
    builder.addCase(
      exportEnterpriseDailyReport.fulfilled,
      handleExportJSONResponse,
    );
    builder.addCase(exportEnterpriseDailyReport.rejected, (state, action) => {
      console.log('Unknown error', action);
      return {
        ...state,
        isLoading: false,
        isSubmitting: false,
      };
    });
  },
});

export const { fetching, fetched, error } =
  exportEnterpriseDailyReportSlice.actions;

export default exportEnterpriseDailyReportSlice.reducer;
